import { useState } from "react";
import { Link } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import { GlassCard, PillTag, Btn, SectionTitle, Avatar } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";

const PROMPTS = [
  "Where in your body do you feel God's strength today?",
  "What weight is the Lord asking you to lay down?",
  "Name one sister who needs your prayer this morning.",
];

export default function Word() {
  const [reflection, setReflection] = useState("");
  const [selectedPrompt, setSelectedPrompt] = useState(0);
  const [completed, setCompleted] = useState(false);

  return (
    <div className="pb-6">
      <ScreenHeader
        variant="cream"
        eyebrow="Saturday morning · 6:00 am"
        title={<>The Word<br /><span className="italic">for today.</span></>}
        subtitle="Three minutes with the Lord before the world begins."
        trailing={
          <PillTag tone="gold">Day 47 streak</PillTag>
        }
      />

      <div className="px-5 space-y-4">
        {/* Verse card — illustrated background */}
        <GlassCard organic padding={0} style={{ overflow: "hidden", background: "linear-gradient(165deg, #1E4A35 0%, #163828 100%)", color: "#F5F0E8", border: "1px solid rgba(245,240,232,0.06)" }}>
          <div className="relative p-6">
            <div className="mnf-orb" style={{ background: "rgba(212,160,78,0.20)" }} />
            {/* quiet tree silhouette */}
            <svg
              className="absolute right-0 bottom-0 opacity-15"
              width={180}
              height={180}
              viewBox="0 0 180 180"
              aria-hidden
            >
              <path d="M90 20 C 60 50, 50 90, 70 130 L70 160 L110 160 L110 130 C 130 90, 120 50, 90 20 Z" fill="#F5F0E8" />
              <line x1={90} y1={130} x2={90} y2={170} stroke="#F5F0E8" strokeWidth={3} opacity={0.6} />
            </svg>

            <p className="mnf-eyebrow relative" style={{ color: "rgba(229,185,151,0.95)" }}>Proverbs 31 · 25</p>
            <p className="mnf-serif italic relative" style={{ fontSize: 26, fontWeight: 500, lineHeight: 1.25, marginTop: 12, color: "#F5F0E8", letterSpacing: "-0.005em" }}>
              "She is clothed with strength and dignity, and she laughs without fear of the future."
            </p>
            <p className="mnf-body relative mt-4" style={{ fontSize: 11, color: "rgba(245,240,232,0.6)", letterSpacing: "0.04em", textTransform: "uppercase" }}>
              ESV translation
            </p>
          </div>
        </GlassCard>

        {/* Reflection */}
        <SectionTitle eyebrow="Three minute reflection" title="Sit with this" />
        <GlassCard padding={18}>
          <p className="mnf-body" style={{ fontSize: 13.5, color: "var(--mnf-fd)", lineHeight: 1.65 }}>
            The Hebrew root for "strength" here is <span className="mnf-serif italic" style={{ color: "var(--mnf-t)" }}>oz</span>, not the strain of the gym, but the steady might that comes from being rooted. Dignity, <span className="mnf-serif italic" style={{ color: "var(--mnf-t)" }}>hadar</span>, is the visible bloom of that root.
          </p>
          <p className="mnf-body mt-3" style={{ fontSize: 13.5, color: "var(--mnf-fd)", lineHeight: 1.65 }}>
            She laughs at the days to come because her foundation is not in the days. Her tomorrow is the Lord's.
          </p>
          <p className="mnf-body italic mt-4 pt-4" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.55, borderTop: "1px solid rgba(30,74,53,0.08)" }}>
            Tend your body as the temple of this kind of strength today, sister.
          </p>
        </GlassCard>

        {/* Journal prompt */}
        <SectionTitle eyebrow="Journal" title="A prompt to carry" />
        <GlassCard padding={18}>
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {PROMPTS.map((_, i) => (
              <button
                key={i}
                data-testid={`button-prompt-${i}`}
                onClick={() => setSelectedPrompt(i)}
                className="mnf-body transition-all"
                style={{
                  padding: "5px 12px", borderRadius: 999,
                  background: selectedPrompt === i ? "var(--mnf-f)" : "rgba(30,74,53,0.06)",
                  color: selectedPrompt === i ? "#F5F0E8" : "var(--mnf-f)",
                  fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                }}
              >
                Prompt {i + 1}
              </button>
            ))}
          </div>
          <p className="mnf-serif italic" style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.35 }}>
            {PROMPTS[selectedPrompt]}
          </p>
          <textarea
            data-testid="textarea-reflection"
            value={reflection}
            onChange={(e) => setReflection(e.target.value)}
            placeholder="Begin where you are…"
            className="w-full mnf-body bg-transparent outline-none resize-none mt-4"
            rows={5}
            style={{
              fontSize: 14, color: "var(--mnf-fd)", lineHeight: 1.6,
              borderTop: "1px solid rgba(30,74,53,0.08)",
              paddingTop: 12,
            }}
          />
          <div className="flex items-center justify-between mt-3">
            <span className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)" }}>
              {reflection.length} characters · only you see this
            </span>
            <Btn variant="primary" size="sm" onClick={() => setCompleted(true)} disabled={reflection.trim().length < 5} data-testid="button-save-reflection">
              <Icon name="save" size={14} /> Save
            </Btn>
          </div>
          {completed && (
            <div className="mt-3 flex items-center gap-2 mnf-body" style={{ fontSize: 12, color: "#2C6248" }}>
              <Icon name="check_circle" size={14} weight={600} /> Sealed in your journal.
            </div>
          )}
        </GlassCard>

        {/* Share with a sister */}
        <SectionTitle eyebrow="Carry it together" title="Share with a sister" />
        <GlassCard padding={16}>
          <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
            Send this verse straight to a sister who needs it today.
          </p>
          <div className="flex items-center gap-2.5 mt-3 overflow-x-auto pb-1">
            {[
              { initials: "R", name: "Rebecca", ringColor: "rgba(192,120,74,0.45)" },
              { initials: "J", name: "Joanna",  ringColor: "rgba(168,212,184,0.55)" },
              { initials: "M", name: "Mara",    ringColor: "rgba(160,120,32,0.55)" },
              { initials: "B", name: "Bethany", ringColor: "rgba(192,120,74,0.45)" },
              { initials: "+", name: "More",    ringColor: "rgba(30,74,53,0.18)" },
            ].map((s) => (
              <button key={s.name} className="flex flex-col items-center gap-1 shrink-0" style={{ width: 60 }} data-testid={`button-share-${s.initials.toLowerCase()}`}>
                <Avatar initials={s.initials} size={42} ringColor={s.ringColor} />
                <span className="mnf-body" style={{ fontSize: 10.5, color: "var(--mnf-tg)" }}>{s.name}</span>
              </button>
            ))}
          </div>
          <Link href="/share" className="no-underline">
            <Btn variant="coral" block size="md" className="mt-3">
              <Icon name="share" size={15} /> Make a share card
            </Btn>
          </Link>
        </GlassCard>

        {/* Read past words */}
        <Btn variant="secondary" block size="md">
          <Icon name="menu_book" size={16} /> Past words · 47 days
        </Btn>
      </div>
    </div>
  );
}
